import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Gauge, Activity, Signal, ChevronRight } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip } from "recharts";
import { Radial } from "./Radial";
import { CHART_MARGIN, CHART_TICK, CHART_CURSOR, CHART_TOOLTIP_STYLE, CHART_BAR_RADIUS, usageFmt } from "./data";

export function StatRail({ totalRemaining, usedGb, usage, accounts, ispOrders }) {
  const series = usage?.series?.length ? usage.series : [];
  const healthPct = totalRemaining + usedGb > 0 ? totalRemaining / (totalRemaining + usedGb) : 0;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      {/* Bandwidth gauge (dark) */}
      <div className="relative overflow-hidden rounded-3xl p-6 text-white shadow-lg" data-testid="data-remaining-card"
        style={{ background: "radial-gradient(130% 130% at 15% 10%, #10B5A3 0%, #0A6E63 45%, #06302B 100%)" }}>
        <div className="flex items-center justify-between mb-2">
          <span className="font-head text-lg font-semibold">Bandwidth</span>
          <Gauge className="w-5 h-5 opacity-70" aria-hidden="true" />
        </div>
        <div className="flex items-center gap-5">
          <Radial pct={totalRemaining > 0 ? healthPct : 0} color="#7DF5E4">
            <div>
              <div className="font-mono text-2xl font-bold leading-none" data-testid="bandwidth-remaining-stat">{totalRemaining}</div>
              <div className="text-[10px] text-white/70 tracking-widest mt-1">GB LEFT</div>
            </div>
          </Radial>
          <div className="space-y-3 text-sm">
            <div><div className="text-white/60 text-xs">Used (7d)</div><div className="font-mono font-semibold">{usedGb.toFixed(2)} GB</div></div>
            <div><div className="text-white/60 text-xs">Accounts</div><div className="font-mono font-semibold">{accounts.length}</div></div>
            <div><div className="text-white/60 text-xs">ISP orders</div><div className="font-mono font-semibold">{ispOrders.length}</div></div>
          </div>
        </div>
        {totalRemaining === 0 && <div className="mt-3 text-xs text-white/70">No active plan — purchase data to get started.</div>}
      </div>

      {/* Usage bars */}
      <div className="rounded-3xl p-6 bg-white border border-[#E4EEEC] shadow-sm flex flex-col" data-testid="daily-usage-card">
        <div className="flex items-center justify-between">
          <span className="font-head text-lg font-semibold text-ink flex items-center gap-2"><Activity className="w-4 h-4 text-neon" aria-hidden="true" /> Data Usage</span>
          <span className="text-[10px] font-mono tracking-wider text-neon bg-[#E7F7F4] rounded-full px-2 py-1">7D</span>
        </div>
        <div className="mt-2 flex items-baseline gap-1">
          <span className="font-mono text-3xl font-bold text-ink" data-testid="usage-total-gb">{(usage?.total_gb ?? 0).toFixed(2)}</span>
          <span className="text-zinc-400 font-mono text-sm">GB</span>
          <span className="ml-auto text-[11px] font-mono text-zinc-400" data-testid="usage-total-requests">{(usage?.total_requests ?? 0).toLocaleString()} reqs</span>
        </div>
        <div className="mt-2 h-[104px] -mx-1">
          {series.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%" minHeight={104}>
              <BarChart data={series} margin={CHART_MARGIN}>
                <defs><linearGradient id="uBar" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2DD4BF" /><stop offset="100%" stopColor="#0E9E8E" /></linearGradient></defs>
                <XAxis dataKey="date" tick={CHART_TICK} axisLine={false} tickLine={false} interval={0} />
                <Tooltip cursor={CHART_CURSOR} contentStyle={CHART_TOOLTIP_STYLE} formatter={usageFmt} />
                <Bar dataKey="gb" fill="url(#uBar)" radius={CHART_BAR_RADIUS} maxBarSize={20} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-full grid place-items-center text-zinc-400 text-sm">No usage data yet</div>}
        </div>
      </div>

      {/* Network health ring + tier */}
      <div className="rounded-3xl p-6 bg-white border border-[#E4EEEC] shadow-sm flex items-center gap-5">
        <Radial pct={0.999} color="#10B5A3">
          <div><div className="font-mono text-2xl font-bold text-ink leading-none">99.9%</div><div className="text-[10px] text-zinc-400 tracking-widest mt-1">UPTIME</div></div>
        </Radial>
        <div className="flex-1">
          <div className="text-[10px] font-mono tracking-[0.2em] text-zinc-400">MEMBERSHIP</div>
          <div className="font-head text-xl font-extrabold text-ink">Bronze</div>
          <div className="flex items-center gap-1.5 mt-2 text-xs text-zinc-500"><Signal className="w-3.5 h-3.5 text-neon" aria-hidden="true" /> 195 exit locations</div>
          <Button onClick={() => toast.info("Rewards coming soon")} data-testid="redeem-points-button" className="mt-3 h-9 px-4 bg-neon text-white hover:bg-neon-hover rounded-full text-sm font-semibold">Redeem <ChevronRight className="w-3.5 h-3.5 ml-1" /></Button>
        </div>
      </div>
    </div>
  );
}
